import { showToast } from "@/lib/toast";

export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = { ok: false; error: { message: string; code?: string } };
export type ApiResponse<T> = ApiOk<T> | ApiErr;

/**
 * A missing or expired session is not a message to render inline: it asks the
 * shell to send the user back to the login screen. Every other API failure is
 * surfaced through the single top-of-screen popup so surfaces stay free of
 * inline banners.
 */
async function handleApiError(message: string, status: number, code?: string) {
  if (typeof window === "undefined") return;
  if (status === 401 || code === "auth_required" || code === "session_required") {
    // A single endpoint can answer 401 while the session is still valid (a
    // stale tab, a cancelled request). Only leave the page when the session
    // check explicitly reports an unauthenticated session; a failed or
    // interrupted check must not bounce the user.
    let authenticated: boolean | null = null;
    try {
      const res = await fetch("/api/auth/status", { credentials: "include", cache: "no-store" });
      const payload = (await res.json()) as { data?: { authenticated?: boolean } };
      authenticated = typeof payload?.data?.authenticated === "boolean" ? payload.data.authenticated : null;
    } catch {
      authenticated = null;
    }
    if (authenticated === false) {
      // The check itself can race a cold start and answer false once. Confirm
      // again before abandoning the page the user is working in.
      await new Promise((resolve) => setTimeout(resolve, 800));
      try {
        const res = await fetch("/api/auth/status", { credentials: "include", cache: "no-store" });
        const payload = (await res.json()) as { data?: { authenticated?: boolean } };
        authenticated = typeof payload?.data?.authenticated === "boolean" ? payload.data.authenticated : null;
      } catch {
        authenticated = null;
      }
    }
    if (authenticated === false) {
      const next = `${window.location.pathname}${window.location.search}`;
      window.dispatchEvent(new CustomEvent("tairoh-auth-required", { detail: { next } }));
      return;
    }
    showToast(message, "error");
    return;
  }
  showToast(message, "error");
}

/**
 * `silent` keeps background polling failures out of the global popup: a
 * transient 404/500 while the dev server recompiles, or a dropped request,
 * must not interrupt the user. Session errors are still handled.
 */
export type FetchJsonOptions = { silent?: boolean };

function shouldReportFailure(options: FetchJsonOptions | undefined, status: number, code?: string) {
  if (!options?.silent) return true;
  return status === 401 || code === "auth_required" || code === "session_required";
}

export async function fetchJson<T>(input: RequestInfo | URL, init?: RequestInit, options?: FetchJsonOptions): Promise<T> {
  const res = await fetch(input, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = await res.text();
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(body) as ApiResponse<T>;
  } catch {
    const message = res.ok ? "サーバーから正しい応答を受け取れませんでした" : `サーバーエラー（HTTP ${res.status}）`;
    if (shouldReportFailure(options, res.status)) await handleApiError(message, res.status);
    throw new Error(message);
  }
  if (!json.ok) {
    if (shouldReportFailure(options, res.status, json.error.code)) await handleApiError(json.error.message, res.status, json.error.code);
    throw new Error(json.error.message);
  }
  return json.data;
}
